import React, { useRef, useState, useLayoutEffect, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { ChevronRight, ChevronDown, User, Shield, ShieldCheck, UserX, Monitor, Accessibility, LifeBuoy, LogOut, Loader2 } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { Avatar } from "../Avatar";
import { RankBadge } from "../RankBadge";
import { VerifiedBadge } from "../VerifiedBadge";
import { nameColorClass } from "../../lib/cosmetics";

const W = 288; // w-72
const GAP = 8;

const ITEMS = [
  { id: "profile", label: "My Den", sub: "Your profile & activity", icon: User, to: "/profile" },
  { id: "privacy", label: "Privacy", sub: "Visibility, activity & email", icon: Shield, to: "/privacy-settings" },
  { id: "security", label: "Security", sub: "2FA, passkeys & logins", icon: ShieldCheck, to: "/security" },
  { id: "blocked", label: "Blocked & Muted Wolves", sub: "Wolves you've silenced", icon: UserX, to: "/blocked" },
  { id: "display", label: "Notifications & Presence", sub: "Sounds, alerts & presence", icon: Monitor, to: "/settings" },
  { id: "accessibility", label: "Accessibility", sub: "Text size, contrast & language", icon: Accessibility, to: "/settings/accessibility" },
  { id: "help", label: "Help & Support", sub: "Guides & helpdesk tickets", icon: LifeBuoy, to: "/helpdesk" },
];

export function AccountMenu({ open, onToggle, onClose, myPresence }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const btnRef = useRef(null);
  const [pos, setPos] = useState(null);
  const [confirmLogout, setConfirmLogout] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  useEffect(() => { if (!open) setConfirmLogout(false); }, [open]);

  const compute = useCallback(() => {
    const el = btnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const vw = window.innerWidth, vh = window.innerHeight;
    const top = r.bottom + GAP;
    const left = Math.max(GAP, Math.min(r.right - W, vw - W - GAP));
    const maxH = vh - top - GAP;
    setPos({ top, left, maxH });
  }, []);

  useLayoutEffect(() => {
    if (!open) { setPos(null); return; }
    compute();
    window.addEventListener("resize", compute);
    window.addEventListener("scroll", compute, true);
    return () => {
      window.removeEventListener("resize", compute);
      window.removeEventListener("scroll", compute, true);
    };
  }, [open, compute]);

  const go = (to) => { onClose?.(); navigate(to); };

  const doLogout = async () => {
    setLoggingOut(true);
    try { await logout(); } finally { setLoggingOut(false); }
  };

  return (
    <div className="relative">
      <button
        ref={btnRef}
        data-testid="account-menu-trigger"
        onClick={onToggle}
        aria-expanded={open}
        title="Account"
        className={`flex items-center gap-2 pl-1 pr-1 sm:pr-2 py-1 rounded-full transition-colors ${open ? "bg-slate-800/70" : "hover:bg-slate-800/70"}`}
      >
        <Avatar eager link={false} user={user} size="xs" showPresence presence={myPresence} />
        <div className="hidden sm:block leading-tight text-right max-w-[120px]">
          <div className="text-xs font-semibold text-slate-200 truncate">{user?.name}</div>
          <div className="text-[10px] text-slate-500 truncate">@{user?.username}</div>
        </div>
        <ChevronDown className={`w-4 h-4 shrink-0 text-slate-400 transition-transform ${open ? "rotate-180 text-sky-400" : ""}`} />
      </button>

      {open && pos && createPortal(
        <>
          <div className="fixed inset-0 z-[70]" onClick={onClose} data-testid="account-menu-backdrop" />
          <div
            style={{ top: pos.top, left: pos.left, width: W, maxHeight: pos.maxH }}
            className="fixed z-[72] overflow-y-auto no-scrollbar bg-[#0d141e] border border-slate-700 rounded-2xl shadow-2xl animate-picker-in"
            data-testid="account-menu-dropdown"
          >
            {/* Identity header — informational, not a link */}
            <div className="flex items-center gap-3 px-4 py-4 border-b border-slate-800" data-testid="account-menu-identity">
              <Avatar eager user={user} size="md" />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold truncate flex items-center gap-1">
                  <span className={nameColorClass(user?.equipped_name_color) || "text-slate-100"}>{user?.name}</span>
                  <VerifiedBadge verified={user?.verified} className="w-3.5 h-3.5" />
                </div>
                <div className="mt-1">{user && <RankBadge rank={user.rank} />}</div>
              </div>
            </div>

            {/* Menu items */}
            <div className="py-1">
              {ITEMS.map((m) => (
                <button
                  key={m.id}
                  data-testid={`account-menu-${m.id}`}
                  onClick={() => go(m.to)}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors group hover:bg-slate-800/60"
                >
                  <div className="w-8 h-8 rounded-full bg-sky-500/10 border border-sky-500/25 flex items-center justify-center shrink-0">
                    <m.icon className="w-4 h-4 text-sky-400" />
                  </div>
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-medium text-slate-200">{m.label}</span>
                    <span className="block text-xs text-slate-500 truncate">{m.sub}</span>
                  </span>
                  <ChevronRight className="w-4 h-4 shrink-0 text-slate-500 group-hover:text-sky-400 transition-colors" />
                </button>
              ))}
            </div>

            {/* Log out */}
            <div className="border-t border-slate-800 p-2">
              {!confirmLogout ? (
                <button
                  data-testid="account-menu-logout"
                  onClick={() => setConfirmLogout(true)}
                  className="w-full flex items-center gap-3 px-2 py-2.5 rounded-lg text-left transition-colors hover:bg-red-950/40 group"
                >
                  <div className="w-8 h-8 rounded-full bg-red-500/10 border border-red-500/25 flex items-center justify-center shrink-0">
                    <LogOut className="w-4 h-4 text-red-400" />
                  </div>
                  <span className="flex-1 text-sm font-medium text-slate-200 group-hover:text-red-300">Log Out</span>
                </button>
              ) : (
                <div className="px-2 py-2" data-testid="account-menu-logout-confirm">
                  <p className="text-sm text-slate-300">Leave the sanctuary?</p>
                  <p className="text-xs text-slate-500 mt-0.5">You'll need to sign in again to return.</p>
                  <div className="mt-3 flex items-center gap-2">
                    <button
                      data-testid="account-menu-logout-confirm-btn"
                      onClick={doLogout}
                      disabled={loggingOut}
                      className="inline-flex items-center gap-1.5 bg-red-600 hover:bg-red-500 disabled:opacity-50 text-white text-xs font-semibold rounded-lg px-3 py-1.5"
                    >
                      {loggingOut ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <LogOut className="w-3.5 h-3.5" />} Yes, log out
                    </button>
                    <button
                      data-testid="account-menu-logout-cancel-btn"
                      onClick={() => setConfirmLogout(false)}
                      className="text-xs text-slate-400 hover:text-slate-200 px-2 py-1.5"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>,
        document.body
      )}
    </div>
  );
}
